import { useReport } from './hooks/use-context';
import type { QuestionData, SectionData } from './types';

export const Table = () => {
  const { body, values } = useReport();

  const sections = body?.sections?.filter(
    (section) => section.section_name !== 'Informações' && section.section_name !== 'Outros...',
  );

  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 12fr 2fr 1fr 1fr 8fr 2.5fr 2.5fr',
  };

  const HeadColumns = ({
    label,
    isLast,
  }: {
    label: string;
    isLast?: boolean;
  }) => (
    <div
      style={{
        backgroundColor: '#d3d3d3',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        borderRight: !isLast ? '1px #000 solid' : 'none',
        borderBottom: '1px #000 solid',
        fontSize: '9px',
      }}
    >
      {label}
    </div>
  );

  const HeadVerticalColumns = ({ label }: { label: string }) => (
    <div
      style={{
        backgroundColor: '#d3d3d3',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRight: '1px #000 solid',
        borderBottom: '1px #000 solid',
        fontSize: '9px',
        lineHeight: '1px',
      }}
    >
      <p
        style={{
          transform: 'rotate(-90deg)',
          margin: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </p>
    </div>
  );

  const ValueCells = ({
    label,
    isLast,
  }: {
    label: string;
    isLast?: boolean;
  }) => (
    <div
      style={{
        backgroundColor: '#fff',
        padding: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        borderRight: '1px #000 solid',
        borderBottom: !isLast ? '1px #000 solid' : 'none',
        fontSize: '9px',
        wordBreak: 'break-word',
      }}
    >
      {label}
    </div>
  );

  const Section = ({
    section,
    isEven,
    isFirst,
    isLast,
  }: {
    section: SectionData;
    isEven?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
  }) => {
    // Standard conformity iterators exclude texts (justifications) here
    const orderedQuestions = section?.questions
      .sort((a, b) => a.position - b.position)
      .filter((question: QuestionData) => question?.type.includes('radio'));

    if (!orderedQuestions || orderedQuestions.length === 0) return null;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(60, 1fr)',
          breakInside: !isFirst ? 'avoid' : undefined,
        }}
      >
        <div
          style={{
            height: orderedQuestions
              ? `${(orderedQuestions?.length || 4) * 80}px`
              : 'auto',
            width: 'auto',
            maxWidth: '30px',
            backgroundColor: isEven ? '#d3d3d3' : '#fff',
            borderRight: '1px #000 solid',
            borderBottom: '1px #000 solid',
            overflow: 'hidden',
            gridColumn: '1 / span 2',
          }}
        >
          <div
            style={{
              display: 'flex',
              transform: 'rotate(-90deg)',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              width: '100%',
              maxWidth: '30px',
              height: `${20 + section.section_name.length * 6}px`,
              margin: 0,
            }}
          >
            {section.section_name}
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridColumn: '3 / span 48',
          }}
        >
          {orderedQuestions?.map((question, index) => {
            const getText = () => {
              if (
                question.answers[0]?.option === 'Não Conforme' ||
                question.answers[0]?.option === 'Sim' || 
                question.answers[0]?.option === 'Não' // Just in case it follows similar standard
              ) {
                const globalQuestionIndex = section?.questions.findIndex(
                  (item) => item.question_id === question.question_id,
                );

                if (
                  globalQuestionIndex !== -1 &&
                  section?.questions[globalQuestionIndex + 1]?.type === 'text'
                ) {
                  return section.questions[globalQuestionIndex + 1]
                    ?.answers?.[0]?.content;
                }
              }
              return null;
            };

            return (
              <div
                key={index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '24fr 4fr 2fr 2fr 16fr',
                }}
              >
                <ValueCells label={question.name} />
                <ValueCells
                  label={question.answers[0]?.option === 'Conforme' ? 'X' : ''}
                />
                <ValueCells
                  label={
                    question.answers[0]?.option === 'Não Conforme' ? 'X' : ''
                  }
                />
                <ValueCells label={`${question.weight || '0'}`} />
                <ValueCells
                  label={getText() || question.answers[0]?.comment || ''}
                />
              </div>
            );
          })}
        </div>

        <div
          style={{
            height: 'auto',
            width: 'auto',
            maxWidth: '100px',
            backgroundColor: '#fff',
            borderRight: '1px #000 solid',
            borderBottom: '1px #000 solid',
            overflow: 'hidden',
            gridColumn: '51 / span 5',
          }}
        >
          <div
            style={{
              display: 'flex',
              transform: 'rotate(-90deg)',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: '100px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              width: '100%',
              paddingRight: '40px',
              margin: 0,
            }}
          >
            {section?.percent_score?.toFixed(0)}
          </div>
        </div>

        <div
          style={{
            height: 'auto',
            width: 'auto',
            backgroundColor: '#fff',
            borderBottom: isLast ? '1px #000 solid' : '',
            overflow: 'inherit',
            gridColumn: '56 / span 5',
          }}
        >
          <div
            style={{
              display: 'flex',
              transform: 'rotate(-90deg)',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              width: '100%',
              paddingRight: '40px',
              margin: 0,
              fontWeight: 'bold',
            }}
          >
            {isFirst ? body?.percent_score?.toFixed(0) : ''}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginTop: '1rem', borderTop: '1px solid #000' }}>
      {/* Headers */}
      <div style={gridStyles}>
        <div
          style={{
            backgroundColor: '#d3d3d3',
            borderRight: '1px #000 solid',
            borderBottom: '1px #000 solid',
            height: '30px',
          }}
        ></div>
        <HeadColumns label="Items" />
        <HeadColumns label="Conforme" />
        <HeadColumns label="NC" />
        <HeadVerticalColumns label="Peso" />
        <HeadColumns label="Observações" />
        <HeadColumns label="Parcial" />
        <HeadColumns label="Geral" isLast />
      </div>

      {sections
        ?.sort((a, b) => a.position - b.position)
        .map((section, index, sortedSections) => (
          <Section
            key={`section_${index}`}
            section={section}
            isEven={index % 2 === 0}
            isFirst={index === 0}
            isLast={index === sortedSections.length - 1}
          />
        ))}

      {/* Observações gerais footer embedded to match standard table */}
      <div
        style={{
          padding: '4px',
          fontSize: '10px',
          borderBottom: '1px #000 solid',
        }}
      >
        <div style={{ fontWeight: 'bold', display: 'flex', gap: '8px' }}>
            <span>{values?.outras_situacoes?.name ?? 'Existem outras situações de risco não evidenciadas acima?'}</span>
            <span style={{ fontWeight: 'normal' }}>
                {values?.outras_situacoes?.answers?.[0]?.option === 'Sim'
                ? values?.outras_situacoes?.child_questions?.find((child) =>
                    child.name.toLowerCase().includes('informe'),
                    )?.answers?.[0]?.content || 'Sim (Sem detalhes informados)'
                : values?.outras_situacoes?.answers?.[0]?.option ?? 'Não avaliado'}
            </span>
        </div>

        <div style={{ fontWeight: 'bold', marginTop: '4px' }}>Observações gerais:</div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginTop: '2px'
          }}
        >
            {values?.observacoes_gerais?.answers?.[0]?.content || '-'}
        </div>
      </div>
    </div>
  );
};
